import React, { useState, useEffect } from 'react';
import { ArrowLeft, Save, Plus, Trash2, Edit2, GripVertical, X, Mail, MessageSquare, Phone, Database, Globe, Cloud, Zap, Shield } from 'lucide-react';
import { toast } from 'sonner';
import { saveDesignerTemplate } from '../../services/workflowService';
import type { ConnectorProperty } from '../../services/workflowService';

interface ConnectorBuilderPageProps {
    onBack: () => void;
    connectorId?: string;
    initialData?: {
        name: string;
        description?: string;
        type?: 'manual' | 'trigger' | 'INBOUND' | 'OUTBOUND';
        category?: string;
        icon?: string;
        modelerTemplateIcon?: string;
        implementation?: string;
        properties?: ConnectorProperty[];
    };
}

const PROPERTY_TYPES = [
    { value: 'string', label: 'String' },
    { value: 'number', label: 'Number' },
    { value: 'boolean', label: 'Boolean' },
    { value: 'json', label: 'JSON' },
    { value: 'select', label: 'Select' },
    { value: 'email-template-picker', label: 'Email Template Picker' },
    { value: 'wysiwyg', label: 'Rich Text' },
    { value: 'mapped-json', label: 'Mapped JSON' }
];

const ICONS = [
    { name: 'Mail', icon: <Mail size={20} /> },
    { name: 'MessageSquare', icon: <MessageSquare size={20} /> },
    { name: 'Phone', icon: <Phone size={20} /> },
    { name: 'Database', icon: <Database size={20} /> },
    { name: 'Globe', icon: <Globe size={20} /> },
    { name: 'Cloud', icon: <Cloud size={20} /> },
    { name: 'Zap', icon: <Zap size={20} /> },
    { name: 'Shield', icon: <Shield size={20} /> },
];

const ConnectorBuilderPage: React.FC<ConnectorBuilderPageProps> = ({ onBack, connectorId, initialData }) => {
    const [name, setName] = useState(initialData?.name || 'New Connector');
    const [description, setDescription] = useState(initialData?.description || '');
    const [icon, setIcon] = useState(initialData?.icon || 'Mail');
    const [iconUrl, setIconUrl] = useState(initialData?.modelerTemplateIcon || '');
    const [category, setCategory] = useState(initialData?.category || 'Custom');
    const [implementation, setImplementation] = useState(initialData?.implementation || 'com.pmdg.delegates.GenericDelegate');
    const [type, setType] = useState<'INBOUND' | 'OUTBOUND'>(
        (initialData?.type === 'manual' || initialData?.type === 'INBOUND') ? 'INBOUND' : 'OUTBOUND'
    );

    const [properties, setProperties] = useState<ConnectorProperty[]>(initialData?.properties || []);
    const [editingProperty, setEditingProperty] = useState<ConnectorProperty | null>(null);
    const [isPropertyModalOpen, setIsPropertyModalOpen] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [draggedIndex, setDraggedIndex] = useState<number | null>(null);

    useEffect(() => {
        if (initialData) {
            setName(initialData.name);
            setDescription(initialData.description || '');
            setCategory(initialData.category || 'Custom');
            // Map type safely
            if (initialData.type === 'manual' || initialData.type === 'INBOUND') setType('INBOUND');
            else if (initialData.type === 'trigger' || initialData.type === 'OUTBOUND') setType('OUTBOUND');

            if (initialData.properties) setProperties(initialData.properties);
            if (initialData.icon) setIcon(initialData.icon);
            if (initialData.modelerTemplateIcon) setIconUrl(initialData.modelerTemplateIcon);
            if (initialData.implementation) setImplementation(initialData.implementation);
        }
    }, [initialData]);

    const handleAddProperty = () => {
        setEditingProperty({
            id: '',
            label: '',
            type: 'string',
            required: false,
            placeholder: '',
            parameterType: 'input',
            hidden: false
        });
        setIsPropertyModalOpen(true);
    };

    const handleEditProperty = (prop: ConnectorProperty) => {
        setEditingProperty({ ...prop });
        setIsPropertyModalOpen(true);
    };

    const handleDeleteProperty = (id: string) => {
        setProperties(properties.filter(p => p.id !== id));
    };

    const handleSaveProperty = () => {
        if (!editingProperty) return;
        if (!editingProperty.id || !editingProperty.label) {
            toast.error('ID and Label are required');
            return;
        }

        setProperties(prev => {
            const idx = prev.findIndex(p => p.id === editingProperty.id);
            if (idx >= 0) {
                // Replace existing
                const newProps = [...prev];
                newProps[idx] = editingProperty;
                return newProps;
            }
            // Add new
            return [...prev, editingProperty];
        });

        setIsPropertyModalOpen(false);
        setEditingProperty(null);
    };

    // Drag and Drop Handlers
    const handleDragStart = (e: React.DragEvent, index: number) => {
        setDraggedIndex(index);
        e.dataTransfer.effectAllowed = "move";
    };

    const handleDragOver = (e: React.DragEvent, index: number) => {
        e.preventDefault(); // Necessary for Drop to work
        if (draggedIndex === null) return;
        if (draggedIndex !== index) {
            // Optional: Visual feedback of move could go here, but simple swap on drop is safer for now
        }
    };

    const handleDrop = (e: React.DragEvent, index: number) => {
        e.preventDefault();
        if (draggedIndex === null) return;

        const newProperties = [...properties];
        const [draggedItem] = newProperties.splice(draggedIndex, 1);
        newProperties.splice(index, 0, draggedItem);

        setProperties(newProperties);
        setDraggedIndex(null);
    };

    const handleSaveConnector = async () => {
        if (!name.trim()) {
            toast.error("Connector name is required");
            return;
        }

        setIsSaving(true);
        try {
            // Construct the inner content (the detailed JSON)
            const connectorData = {
                type: name.toLowerCase().replace(/\s+/g, '-'), // Internal topic/type identifier
                name: name,
                icon: icon,
                color: "blue", // Default color
                description: description,
                modelerTemplateIcon: iconUrl || "https://cdn-icons-png.freepik.com/128/16372/16372081.png",
                implementation: implementation,
                topic: name.toLowerCase().replace(/\s+/g, '-'),
                properties: properties
            };

            await saveDesignerTemplate({
                id: connectorId, // If editing existing
                name: name,
                category: category,
                type: type, // INBOUND or OUTBOUND as per Enum
                jsonContent: undefined, // Let service handle encoding
                decodedContent: connectorData as any
            });
            toast.success("Connector saved successfully");
            onBack();
        } catch (e) {
            console.error(e);
            toast.error("Failed to save connector");
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="flex flex-col h-screen bg-gray-50">
            {/* Header */}
            <div className="h-16 bg-white border-b border-gray-200 px-6 flex items-center justify-between shadow-sm z-10">
                <div className="flex items-center gap-4">
                    <button onClick={onBack} className="p-2 hover:bg-gray-100 rounded-lg text-gray-500 transition-colors">
                        <ArrowLeft size={20} />
                    </button>

                    <div className="flex items-center gap-3">
                        {/* Icon Picker */}



                        <div>
                            <h1 className="text-lg font-bold text-gray-900">{name || 'New Connector'}</h1>
                            <div className="flex items-center gap-2">
                                <span className={`text-xs px-2 py-0.5 rounded-md font-medium ${type === 'INBOUND' ? 'bg-indigo-100 text-indigo-700' : 'bg-orange-100 text-orange-700'}`}>
                                    {type === 'INBOUND' ? 'Manual' : 'Trigger'}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="flex items-center gap-3">
                    <button
                        onClick={handleSaveConnector}
                        disabled={isSaving}
                        className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50"
                    >
                        <Save size={16} />
                        {isSaving ? 'Saving...' : 'Save Connector'}
                    </button>
                </div>
            </div>

            {/* Content */}
            <div className="flex-1 flex overflow-hidden">
                {/* Properties Editor (Left) */}
                <div className="w-2/5 border-r border-gray-200 bg-white flex flex-col overflow-hidden">

                    {/* Metadata Section */}
                    <div className="p-6 border-b border-gray-100 bg-gray-50/30">
                        <h3 className="text-sm font-medium text-gray-700 mb-2">General Settings</h3>
                        <div className="space-y-3">
                            <div>
                                <label className="text-xs text-gray-500 mb-1 block">Name</label>
                                <input
                                    type="text"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500 font-bold"
                                    placeholder="Connector Name"
                                />
                            </div>
                            <div>
                                <label className="text-xs text-gray-500 mb-1 block">Description</label>
                                <textarea
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500 resize-none h-20"
                                    placeholder="Enter connector description..."
                                />
                            </div>
                            <div>
                                <label className="text-xs text-gray-500 mb-1 block">Category</label>
                                <input
                                    type="text"
                                    value={category}
                                    onChange={(e) => setCategory(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
                                    placeholder="e.g. Communication"
                                />
                            </div>
                            <div>
                                <label className="text-xs text-gray-500 mb-1 block">Implementation</label>
                                <input
                                    type="text"
                                    value={implementation}
                                    onChange={(e) => setImplementation(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500 font-mono text-xs"
                                    placeholder="com.package.DelegateClass"
                                />
                            </div>
                            <div>
                                <label className="text-xs text-gray-500 mb-1 block">Icon URL (Optional)</label>
                                <input
                                    type="text"
                                    value={iconUrl}
                                    onChange={(e) => setIconUrl(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500 font-mono text-xs"
                                    placeholder="https://example.com/icon.png"
                                />
                            </div>
                        </div>
                    </div>

                    <div className="p-4 border-b border-gray-100 flex items-center justify-between bg-white pt-6">
                        <h2 className="font-semibold text-gray-900">Form Properties</h2>
                        <button
                            onClick={handleAddProperty}
                            className="p-1.5 bg-indigo-50 text-indigo-600 rounded-md hover:bg-indigo-100 transition-colors flex items-center gap-1 text-xs font-medium px-3"
                        >
                            <Plus size={16} />
                            Add Field
                        </button>
                    </div>

                    <div className="flex-1 overflow-y-auto p-4 space-y-2">
                        {properties.length === 0 ? (
                            <div className="text-center py-12 text-gray-400 bg-gray-50 rounded-xl border border-dashed border-gray-200 mx-2">
                                <p className="text-sm">No properties defined.</p>
                                <button onClick={handleAddProperty} className="text-indigo-600 font-medium mt-2 hover:underline text-sm">Add your first field</button>
                            </div>
                        ) : (
                            properties.map((prop, index) => (
                                <div
                                    key={prop.id || index}
                                    draggable
                                    onDragStart={(e) => handleDragStart(e, index)}
                                    onDragOver={(e) => handleDragOver(e, index)}
                                    onDrop={(e) => handleDrop(e, index)}
                                    className={`group bg-white border border-gray-200 rounded-lg p-3 hover:border-indigo-300 hover:shadow-sm transition-all flex items-center gap-3 cursor-move ${draggedIndex === index ? 'opacity-50 border-dashed border-indigo-400' : ''}`}
                                >
                                    <GripVertical size={16} className="text-gray-300 cursor-grab" />
                                    <div className="flex-1 min-w-0">
                                        <div className="font-medium text-gray-900 truncate flex items-center gap-2">
                                            {prop.label}
                                            <span className="text-gray-400 font-normal text-xs font-mono bg-gray-50 px-1 rounded">#{prop.id}</span>
                                        </div>
                                        <div className="text-xs text-gray-500 flex gap-2 mt-1">
                                            <span className="bg-gray-100 px-1.5 rounded text-gray-600">{prop.type}</span>
                                            {prop.required && <span className="text-red-600 bg-red-50 px-1.5 rounded">Required</span>}
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-1 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
                                        <button onClick={() => handleEditProperty(prop)} className="p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded" title="Edit">
                                            <Edit2 size={14} />
                                        </button>
                                        <button onClick={() => handleDeleteProperty(prop.id)} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded" title="Delete">
                                            <Trash2 size={14} />
                                        </button>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>

                {/* Live Preview (Right) */}
                <div className="flex-1 bg-gray-50/50 p-8 overflow-y-auto">
                    <div className="max-w-md mx-auto">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">Preview</h3>
                        </div>

                        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                            <div className="bg-gradient-to-r from-gray-50 to-white px-6 py-5 border-b border-gray-100">
                                <div className="flex items-center gap-4">
                                    <div className="w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center text-indigo-600 shadow-sm overflow-hidden">
                                        {iconUrl ? (
                                            <img src={iconUrl} alt="icon" className="w-8 h-8 object-contain" />
                                        ) : (
                                            ICONS.find(i => i.name === icon)?.icon || <Mail size={24} />
                                        )}
                                    </div>
                                    <div>
                                        <h3 className="text-lg font-bold text-gray-900">{name || "Connector Name"}</h3>
                                        <p className="text-sm text-gray-500 line-clamp-2">{description || "No description provided."}</p>
                                    </div>
                                </div>
                            </div>

                            <div className="p-6 space-y-5">
                                {properties.map(prop => {
                                    if (prop.hidden) return null;
                                    return (
                                        <div key={prop.id} className="space-y-1.5 animate-in fade-in duration-300">
                                            <label className="block text-sm font-medium text-gray-700">
                                                {prop.label} {prop.required && <span className="text-red-500">*</span>}
                                            </label>
                                            {prop.type === 'string' || prop.type === 'number' ? (
                                                <input
                                                    type={prop.type === 'number' ? 'number' : 'text'}
                                                    placeholder={prop.placeholder}
                                                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all"
                                                    disabled
                                                />
                                            ) : prop.type === 'boolean' ? (
                                                <div className="flex items-center gap-2">
                                                    <input type="checkbox" className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" disabled />
                                                    <span className="text-sm text-gray-600">{prop.placeholder || 'Enabled'}</span>
                                                </div>
                                            ) : prop.type === 'select' ? (
                                                <select className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-gray-50 focus:outline-none" disabled>
                                                    <option>{prop.placeholder || 'Select an option'}</option>
                                                </select>
                                            ) : (
                                                <div className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-gray-50 text-gray-400 italic">
                                                    {prop.type} component
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                                {properties.length === 0 && (
                                    <div className="text-center py-8 text-gray-400">
                                        <Zap size={24} className="mx-auto mb-2 opacity-50" />
                                        <p className="text-sm">Properties will appear here</p>
                                    </div>
                                )}
                            </div>

                            <div className="bg-gray-50 px-6 py-4 border-t border-gray-100 flex justify-end">
                                <button className="px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg opacity-50 cursor-not-allowed">
                                    Action
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Property Modal */}
            {isPropertyModalOpen && editingProperty && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-in fade-in duration-200">
                    <div className="bg-white rounded-xl shadow-xl w-full max-w-lg overflow-hidden flex flex-col max-h-[90vh]">
                        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
                            <h3 className="font-semibold text-gray-900">Property Configuration</h3>
                            <button onClick={() => setIsPropertyModalOpen(false)} className="text-gray-400 hover:text-gray-600 transition-colors">
                                <X size={20} />
                            </button>
                        </div>

                        <div className="flex-1 overflow-y-auto p-6 space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-1.5">
                                    <label className="text-xs font-medium text-gray-700">ID (Key)</label>
                                    <input
                                        type="text"
                                        value={editingProperty.id}
                                        onChange={e => setEditingProperty({ ...editingProperty, id: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
                                        placeholder="emailProvider"
                                        autoFocus
                                    />
                                </div>
                                <div className="space-y-1.5">
                                    <label className="text-xs font-medium text-gray-700">Label (Display Name)</label>
                                    <input
                                        type="text"
                                        value={editingProperty.label}
                                        onChange={e => setEditingProperty({ ...editingProperty, label: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
                                        placeholder="Email Provider"
                                    />
                                </div>
                            </div>

                            <div className="space-y-1.5">
                                <label className="text-xs font-medium text-gray-700">Type</label>
                                <select
                                    value={editingProperty.type}
                                    onChange={e => setEditingProperty({ ...editingProperty, type: e.target.value as any })}
                                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500 bg-white"
                                >
                                    {PROPERTY_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                                </select>
                            </div>

                            <div className="space-y-1.5">
                                <label className="text-xs font-medium text-gray-700">Placeholder</label>
                                <input
                                    type="text"
                                    value={editingProperty.placeholder || ''}
                                    onChange={e => setEditingProperty({ ...editingProperty, placeholder: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
                                    placeholder="Enter placeholder text..."
                                />
                            </div>

                            <div className="flex items-center gap-4 pt-2">
                                <label className="flex items-center gap-2 cursor-pointer group">
                                    <input
                                        type="checkbox"
                                        checked={editingProperty.required}
                                        onChange={e => setEditingProperty({ ...editingProperty, required: e.target.checked })}
                                        className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                    />
                                    <span className="text-sm text-gray-700 group-hover:text-indigo-600 transition-colors">Required</span>
                                </label>
                                <label className="flex items-center gap-2 cursor-pointer group">
                                    <input
                                        type="checkbox"
                                        checked={editingProperty.hidden}
                                        onChange={e => setEditingProperty({ ...editingProperty, hidden: e.target.checked })}
                                        className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                    />
                                    <span className="text-sm text-gray-700 group-hover:text-indigo-600 transition-colors">Hidden</span>
                                </label>
                            </div>

                            {/* Advanced / Logic Section */}
                            <div className="pt-2 border-t border-gray-100">
                                <p className="text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wide">Conditional Logic (Show When)</p>
                                <div className="bg-gray-50 p-3 rounded-lg border border-gray-100 space-y-2">
                                    <div className="grid grid-cols-2 gap-2">
                                        <input
                                            type="text"
                                            placeholder="Dependent Field ID"
                                            value={editingProperty.showWhen?.field || ''}
                                            onChange={(e) => setEditingProperty({
                                                ...editingProperty,
                                                showWhen: { field: e.target.value, value: editingProperty.showWhen?.value || [] }
                                            })}
                                            className="px-2 py-1.5 text-xs border border-gray-200 rounded focus:outline-none focus:border-indigo-500"
                                        />
                                        <input
                                            type="text"
                                            placeholder="Value (comma separated)"
                                            value={editingProperty.showWhen?.value?.join(',') || ''}
                                            onChange={(e) => setEditingProperty({
                                                ...editingProperty,
                                                showWhen: {
                                                    field: editingProperty.showWhen?.field || '',
                                                    value: e.target.value.split(',').map(v => v.trim()).filter(Boolean)
                                                }
                                            })}
                                            className="px-2 py-1.5 text-xs border border-gray-200 rounded focus:outline-none focus:border-indigo-500"
                                        />
                                    </div>
                                    <p className="text-[10px] text-gray-400">Example: dependent key 'emailProvider' value 'outlook'</p>
                                </div>
                            </div>
                        </div>

                        <div className="p-4 border-t border-gray-100 bg-gray-50 flex justify-end gap-2">
                            <button onClick={() => setIsPropertyModalOpen(false)} className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">Cancel</button>
                            <button onClick={handleSaveProperty} className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors">Save Property</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ConnectorBuilderPage;
